import * as types from '../mutation-types';
import ContactAPI from '../../api/contacts';
import ConversationApi from '../../api/conversations';
import camelcaseKeys from 'camelcase-keys';

export const createMessagePayload = (payload, message) => {
  const {
    content,
    cc_emails,
    bcc_emails,
    to_emails,
    from_email,
    content_attributes,
    private: isPrivate,
  } = message;

  payload.append('message[content]', content);
  if (cc_emails) payload.append('message[cc_emails]', cc_emails);
  if (bcc_emails) payload.append('message[bcc_emails]', bcc_emails);
  if (to_emails) payload.append('message[to_emails]', to_emails);
  if (from_email) payload.append('message[from_email]', from_email);
  if (isPrivate) payload.append('message[private]', 'true');

  // Handle content_attributes for email channels with HTML content
  if (content_attributes && content_attributes.html_content) {
    payload.append(
      'message[content_attributes][email][html_content][full]',
      content_attributes.html_content
    );
  }
};

export const createConversationPayload = ({ params, contactId, files }) => {
  const { inboxId, message, sourceId, mailSubject, assigneeId, status } =
    params;
  const payload = new FormData();

  if (message) {
    createMessagePayload(payload, message);
  }

  if (files && files.length > 0) {
    files.forEach(file => payload.append('message[attachments][]', file));
  }
  payload.append('inbox_id', inboxId);
  payload.append('status', status || 'open');
  payload.append('contact_id', contactId);
  payload.append('source_id', sourceId);
  // FormData turns undefined into the string "undefined", which the previews
  // would then show as the subject of every non-email conversation.
  if (mailSubject) {
    payload.append('additional_attributes[mail_subject]', mailSubject);
  }
  payload.append('additional_attributes[outgoing_email]', '1');
  if (assigneeId) {
    payload.append('assignee_id', assigneeId);
  }
  if (params.messageDraftId) {
    payload.append('message_draft_id', params.messageDraftId);
  }

  return payload;
};

export const createWhatsAppConversationPayload = ({ params }) => {
  const { inboxId, message, contactId, sourceId, assigneeId } = params;

  const payload = {
    inbox_id: inboxId,
    contact_id: contactId,
    source_id: sourceId,
    message,
    assignee_id: assigneeId,
  };

  return payload;
};

const setNewConversationPayload = ({
  isFromWhatsApp,
  params,
  contactId,
  files,
}) => {
  if (isFromWhatsApp) {
    return createWhatsAppConversationPayload({ params });
  }
  return createConversationPayload({
    params,
    contactId,
    files,
  });
};

const state = {
  records: {},
  uiFlags: {
    isFetching: false,
  },
};

export const getters = {
  getUIFlags($state) {
    return $state.uiFlags;
  },
  getContactConversation: $state => id => {
    return $state.records[Number(id)] || [];
  },
  getAllConversationsByContactId: $state => id => {
    const records = $state.records[Number(id)] || [];
    return camelcaseKeys(records, { deep: true });
  },
};

export const actions = {
  create: async ({ commit }, { params, isFromWhatsApp }) => {
    commit(types.default.SET_CONTACT_CONVERSATIONS_UI_FLAG, {
      isCreating: true,
    });
    const { contactId, files } = params;
    try {
      const payload = setNewConversationPayload({
        isFromWhatsApp,
        params,
        contactId,
        files,
      });

      const { data } = await ConversationApi.create(payload);
      commit(types.default.ADD_CONTACT_CONVERSATION, {
        id: contactId,
        data,
      });

      return data;
    } finally {
      commit(types.default.SET_CONTACT_CONVERSATIONS_UI_FLAG, {
        isCreating: false,
      });
    }
  },
  get: async ({ commit }, contactId) => {
    commit(types.default.SET_CONTACT_CONVERSATIONS_UI_FLAG, {
      isFetching: true,
    });
    try {
      const response = await ContactAPI.getConversations(contactId);
      commit(types.default.SET_CONTACT_CONVERSATIONS, {
        id: contactId,
        data: response.data.payload,
      });
      commit(types.default.SET_CONTACT_CONVERSATIONS_UI_FLAG, {
        isFetching: false,
      });
    } catch (error) {
      commit(types.default.SET_CONTACT_CONVERSATIONS_UI_FLAG, {
        isFetching: false,
      });
    }
  },
  // Websocket conversation payloads, so the history and the open conversations
  // banner follow status changes without refetching.
  upsertConversation: ({ commit }, conversation) => {
    // Without the contact there is no telling which history it belongs to.
    if (!conversation?.id || !conversation.meta?.sender?.id) return;
    commit(types.default.UPSERT_CONTACT_CONVERSATION, conversation);
  },
  removeConversation: ({ commit }, conversationId) => {
    if (!conversationId) return;
    commit(types.default.REMOVE_CONTACT_CONVERSATION, conversationId);
  },
  // A conversation added from the websocket arrives before its first message,
  // so the card preview needs the messages too.
  addMessage: ({ commit }, message) => {
    if (!message?.conversation_id) return;
    commit(types.default.ADD_CONTACT_CONVERSATION_MESSAGE, message);
  },
};

const isSameConversation = (conversation, conversationId) =>
  Number(conversation.id) === Number(conversationId);

const withoutConversation = (conversations, conversationId) =>
  conversations.some(item => isSameConversation(item, conversationId))
    ? conversations.filter(item => !isSameConversation(item, conversationId))
    : conversations;

// Runs `update` on every fetched history. `records` is only replaced when a
// history changed, so events for other contacts don't re-render the lists.
const updateHistories = ($state, update) => {
  let changed = false;
  const records = Object.fromEntries(
    Object.entries($state.records).map(([contactId, conversations]) => {
      const updated = update(conversations, Number(contactId));
      changed = changed || updated !== conversations;
      return [contactId, updated];
    })
  );
  if (changed) $state.records = records;
};

export const mutations = {
  [types.default.SET_CONTACT_CONVERSATIONS_UI_FLAG]($state, data) {
    $state.uiFlags = {
      ...$state.uiFlags,
      ...data,
    };
  },
  [types.default.SET_CONTACT_CONVERSATIONS]: ($state, { id, data }) => {
    $state.records = {
      ...$state.records,
      [id]: data,
    };
  },
  [types.default.ADD_CONTACT_CONVERSATION]: ($state, { id, data }) => {
    const conversations = $state.records[id] || [];

    const updatedConversations = [...conversations];
    const index = conversations.findIndex(
      conversation => conversation.id === data.id
    );

    if (index !== -1) {
      updatedConversations[index] = { ...conversations[index], ...data };
    } else {
      updatedConversations.push(data);
    }

    $state.records = {
      ...$state.records,
      [id]: updatedConversations,
    };
  },
  [types.default.DELETE_CONTACT_CONVERSATION]: ($state, id) => {
    const { [id]: deletedRecord, ...remainingRecords } = $state.records;
    $state.records = remainingRecords;
  },
  // Only histories that were fetched are touched: a list holding just this one
  // conversation would pass for the contact's whole history.
  [types.default.UPSERT_CONTACT_CONVERSATION]: ($state, conversation) => {
    const contactId = Number(conversation.meta.sender.id);
    updateHistories($state, (conversations, historyContactId) => {
      // A conversation moved to another contact (merge) leaves the old history.
      if (historyContactId !== contactId) {
        return withoutConversation(conversations, conversation.id);
      }
      const index = conversations.findIndex(item =>
        isSameConversation(item, conversation.id)
      );
      if (index === -1) return [conversation, ...conversations];
      return conversations.map((item, itemIndex) =>
        itemIndex === index ? { ...item, ...conversation } : item
      );
    });
  },
  [types.default.REMOVE_CONTACT_CONVERSATION]: ($state, conversationId) => {
    updateHistories($state, conversations =>
      withoutConversation(conversations, conversationId)
    );
  },
  [types.default.ADD_CONTACT_CONVERSATION_MESSAGE]: ($state, message) => {
    const { conversation_id: conversationId, conversation = {} } = message;
    updateHistories($state, conversations => {
      const index = conversations.findIndex(item =>
        isSameConversation(item, conversationId)
      );
      const current = conversations[index];
      if (!current || current.messages?.some(({ id }) => id === message.id)) {
        return conversations;
      }
      const updated = {
        ...current,
        messages: [...(current.messages || []), message],
      };
      if (conversation.last_activity_at) {
        updated.last_activity_at = conversation.last_activity_at;
        updated.timestamp = conversation.last_activity_at;
      }
      if (conversation.unread_count !== undefined) {
        updated.unread_count = conversation.unread_count;
      }
      return conversations.map((item, itemIndex) =>
        itemIndex === index ? updated : item
      );
    });
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
